import { motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { MarketingNav } from "@/components/layout/MarketingNav";
import {
  ShieldCheck,
  Activity,
  Target,
  CheckCircle2,
  AlertTriangle,
  BarChart3,
  ClipboardList,
  ArrowRight,
  Building2,
  FileCheck,
} from "lucide-react";

const C = {
  navy: "#07081a",
  navyMid: "#0d0f2d",
  purple: "#6C63FF",
  lime: "#a8e063",
  white: "#ffffff",
  slate: "#c4c9e8",
  dimSlate: "#7880a8",
  border: "rgba(108,99,255,0.2)",
  glassBg: "rgba(11,13,40,0.7)",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

// ─── Hero Snapshot ─────────────────────────────────────────────────────────────
const ComplianceHeroSnapshot = () => (
  <div className="w-full rounded-xl overflow-hidden" style={{ background: C.glassBg, border: `1px solid ${C.border}`, backdropFilter: "blur(12px)" }}>
    <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: `1px solid ${C.border}` }}>
      <ShieldCheck size={14} style={{ color: C.lime }} />
      <span className="text-xs font-semibold" style={{ color: C.slate }}>Compliance Dashboard</span>
      <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ background: `${C.lime}18`, color: C.lime }}>86% Health</span>
    </div>
    <div className="p-3 grid grid-cols-2 gap-2 mb-2">
      {[
        { label: "Health Score", value: "86%", color: C.lime, icon: <Activity size={13} /> },
        { label: "Policy Score", value: "56/100", color: "#f59e0b", icon: <Target size={13} /> },
        { label: "Tasks Done", value: "18/21", color: C.purple, icon: <CheckCircle2 size={13} /> },
        { label: "Overdue", value: "1", color: "#f87171", icon: <AlertTriangle size={13} /> },
      ].map((s, i) => (
        <div key={i} className="rounded-lg p-2.5" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${C.border}` }}>
          <div className="flex items-center gap-1 mb-1" style={{ color: s.color }}>{s.icon}<span className="text-[9px]">{s.label}</span></div>
          <div className="text-base font-bold" style={{ color: s.color }}>{s.value}</div>
        </div>
      ))}
    </div>
    <div className="px-3 pb-3">
      <div className="text-[10px] mb-2 font-semibold" style={{ color: C.dimSlate }}>Department Health</div>
      {[
        { dept: "IT Security", score: 94, color: C.lime },
        { dept: "HR & People", score: 78, color: "#f59e0b" },
        { dept: "Operations", score: 61, color: "#f87171" },
      ].map((d, i) => (
        <div key={i} className="mb-1.5">
          <div className="flex justify-between text-[9px] mb-0.5" style={{ color: C.dimSlate }}>
            <span>{d.dept}</span><span style={{ color: d.color }}>{d.score}%</span>
          </div>
          <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
            <div className="h-full rounded-full" style={{ width: `${d.score}%`, background: d.color, opacity: 0.8 }} />
          </div>
        </div>
      ))}
    </div>
    <div className="px-3 pb-3">
      <div className="text-[10px] mb-1.5 font-semibold" style={{ color: C.dimSlate }}>Compliance Tasks</div>
      {[
        { t: "Conduct Regular Safety Audits", p: "P1", done: false },
        { t: "Evaluate Training Effectiveness", p: "P2", done: true },
        { t: "Assess Training Needs Annually", p: "P1", done: false },
      ].map((task, i) => (
        <div key={i} className="flex items-center gap-2 py-1.5">
          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: task.done ? C.lime : "transparent", border: `2px solid ${task.done ? C.lime : C.dimSlate}` }} />
          <span className="text-[10px] flex-1" style={{ color: task.done ? C.dimSlate : C.slate, textDecoration: task.done ? "line-through" : "none" }}>{task.t}</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: task.p === "P1" ? "rgba(248,113,113,0.15)" : "rgba(108,99,255,0.15)", color: task.p === "P1" ? "#f87171" : C.purple }}>{task.p}</span>
        </div>
      ))}
    </div>
  </div>
);

// ─── Feature Cards ─────────────────────────────────────────────────────────────
const features = [
  { icon: <Activity size={20} />, color: C.lime, title: "Compliance Health Score", desc: "Real-time scoring of your overall compliance posture across policies, tasks, and departments." },
  { icon: <ClipboardList size={20} />, color: C.purple, title: "Auto-Generated Tasks", desc: "Approved policies automatically generate trackable compliance tasks — no manual setup needed." },
  { icon: <Building2 size={20} />, color: "#a78bfa", title: "Department Visibility", desc: "See exactly which departments are on track and which need attention, at a glance." },
  { icon: <AlertTriangle size={20} />, color: "#f87171", title: "Priority Alerts", desc: "Surface overdue items, expiring policies, and compliance gaps before they become audit risks." },
  { icon: <BarChart3 size={20} />, color: "#f59e0b", title: "Policy Coverage Metrics", desc: "Track acknowledgment rates across teams. Know who has seen and accepted each policy." },
  { icon: <FileCheck size={20} />, color: C.lime, title: "Audit-Ready Reporting", desc: "Export compliance snapshots and task completion reports in one click — always audit-ready." },
];

const steps = [
  { num: "01", title: "Assign", desc: "Policies are converted into priority-tagged compliance tasks and assigned to responsible owners." },
  { num: "02", title: "Track", desc: "Monitor completion rates, health scores, and overdue items across every department." },
  { num: "03", title: "Report", desc: "Export a full compliance posture report for auditors or leadership in seconds." },
];

const stats = [
  { value: "4×", label: "Faster task closure" },
  { value: "98%", label: "Acknowledgment rate" },
  { value: "100%", label: "Audit-ready, always" },
  { value: "86%", label: "Avg. health score" },
];

export default function ComplianceFeature() {
  return (
    <div style={{ background: C.navy, minHeight: "100vh", fontFamily: "'DM Sans', ui-sans-serif, sans-serif", color: C.white }}>
      <MarketingNav />

      {/* ── Hero ─────────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden pt-32 pb-24 px-6 md:px-16 lg:px-24">
        {/* Background glow */}
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(ellipse 60% 50% at 20% 40%, rgba(168,224,99,0.07) 0%, transparent 70%)` }} />

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <div>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold mb-6"
              style={{ background: `${C.lime}14`, border: `1px solid ${C.lime}30`, color: C.lime }}>
              <ShieldCheck size={13} /> Compliance Module
            </motion.div>
            <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
              className="text-4xl md:text-5xl font-bold leading-[1.15] mb-5"
              style={{ letterSpacing: "-0.03em" }}>
              Comply Faster.<br />
              <span style={{ color: C.lime }}>Never Miss a Deadline.</span>
            </motion.h1>
            <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
              className="text-base mb-8 max-w-md leading-relaxed" style={{ color: C.slate }}>
              Convert approved policies into trackable tasks, monitor health scores by department, and stay perpetually audit-ready — without the spreadsheet chaos.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3} className="flex flex-wrap gap-3">
              <NavLink to="/auth"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: C.lime, color: C.navy }}>
                Start Free Trial <ArrowRight size={15} />
              </NavLink>
              <NavLink to="/consulting"
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:opacity-80"
                style={{ border: `1px solid ${C.border}`, color: C.slate }}>
                Request a Demo
              </NavLink>
            </motion.div>
          </div>

          {/* Right — mock UI */}
          <motion.div variants={fadeUp} initial="hidden" animate="show" custom={2}>
            <ComplianceHeroSnapshot />
          </motion.div>
        </div>
      </section>

      {/* ── Stats Bar ────────────────────────────────────────────────────────── */}
      <section className="py-10 px-6 md:px-16 lg:px-24" style={{ borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}` }}>
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="text-center">
              <div className="text-3xl font-bold mb-1" style={{ color: C.lime }}>{s.value}</div>
              <div className="text-sm" style={{ color: C.dimSlate }}>{s.label}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── Key Features ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ letterSpacing: "-0.025em" }}>
              Everything compliance teams need
            </h2>
            <p className="text-base max-w-xl mx-auto" style={{ color: C.slate }}>
              From task generation to department health tracking — your compliance operations, centralised.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}
                className="rounded-xl p-6"
                style={{ background: "rgba(255,255,255,0.025)", border: `1px solid ${C.border}` }}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: `${f.color}16`, color: f.color }}>
                  {f.icon}
                </div>
                <h3 className="text-base font-semibold mb-2" style={{ color: C.white }}>{f.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{f.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24" style={{ background: "rgba(255,255,255,0.015)" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ letterSpacing: "-0.025em" }}>How it works</h2>
            <p className="text-base" style={{ color: C.slate }}>Three steps from policy to audit-ready compliance.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <motion.div key={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} className="relative">
                <div className="text-6xl font-black mb-4 leading-none" style={{ color: `${C.lime}18` }}>{s.num}</div>
                <h3 className="text-xl font-bold mb-2" style={{ color: C.white }}>{s.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: C.dimSlate }}>{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 -right-4 text-2xl" style={{ color: C.border }}>→</div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA Banner ───────────────────────────────────────────────────────── */}
      <section className="py-24 px-6 md:px-16 lg:px-24">
        <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center rounded-2xl px-8 py-16"
          style={{ background: `linear-gradient(135deg, rgba(108,99,255,0.12) 0%, rgba(168,224,99,0.06) 100%)`, border: `1px solid ${C.border}` }}>
          <ShieldCheck size={36} className="mx-auto mb-5" style={{ color: C.lime }} />
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ letterSpacing: "-0.025em" }}>
            Ready to take control of compliance?
          </h2>
          <p className="text-base mb-8 max-w-md mx-auto" style={{ color: C.slate }}>
            Join organisations that have eliminated compliance blind spots with ManagEaze.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <NavLink to="/auth"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: C.lime, color: C.navy }}>
              Start Free Trial <ArrowRight size={15} />
            </NavLink>
            <NavLink to="/consulting"
              className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all hover:opacity-80"
              style={{ border: `1px solid ${C.border}`, color: C.slate }}>
              Request a Demo
            </NavLink>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
